import { memo, useMemo } from 'react';

export interface SparklineProps {
  /** Series values, plotted left→right and auto-scaled to the box. */
  points: number[];
  /**
   * Optional explicit x positions (design-space px). The Figma comp places its
   * vertices at hand-tuned intervals; live data uses even spacing instead.
   */
  xs?: number[];
  width?: number;
  height?: number;
  stroke?: string;
  strokeWidth?: number;
  dotRadius?: number;
  className?: string;
}

/**
 * Reproduces the `Group 19` / `Group 20` sparkline marks from Figma
 * (section 2161:7629) as live, data-driven SVG.
 *
 * Figma exports a flattened cubic-bezier path per cell; this draws the same
 * mark from data using a Catmull-Rom → bezier conversion, so the curve shape,
 * stroke and vertex dots match while the geometry follows the series.
 *
 * No charting runtime on purpose: the Availability page renders ~63 of these
 * at once, and a chart instance per cell is what makes dense tables jank.
 */
function SparklineImpl({
  points,
  xs,
  width = 140,
  height = 18.3634,
  stroke = 'var(--spark-stroke)',
  strokeWidth = 1.5,
  dotRadius = 2.2643,
  className,
}: SparklineProps) {
  const geometry = useMemo(() => {
    if (!points?.length) return null;

    const inset = dotRadius + strokeWidth / 2;
    const min = Math.min(...points);
    const max = Math.max(...points);
    const span = max - min || 1;

    const coords: Array<[number, number]> = points.map((v, i) => {
      const x = xs?.[i] ?? (points.length > 1 ? inset + (i * (width - inset * 2)) / (points.length - 1) : width / 2);
      const y = xs ? v : height - inset - ((v - min) / span) * (height - inset * 2);
      return [x, y];
    });

    return { coords, d: catmullRom(coords) };
  }, [points, xs, width, height, dotRadius, strokeWidth]);

  if (!geometry) return <svg width={width} height={height} className={className} />;

  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      aria-hidden="true"
      focusable="false"
      shapeRendering="geometricPrecision"
      style={{ overflow: 'visible', display: 'block' }}
    >
      <path d={geometry.d} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
      {geometry.coords.map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={dotRadius}
          fill="var(--spark-dot-fill)"
          stroke="var(--spark-dot-stroke)"
          strokeWidth={0.5}
        />
      ))}
    </svg>
  );
}

/** Catmull-Rom spline through every point, emitted as cubic beziers. */
function catmullRom(pts: Array<[number, number]>): string {
  if (pts.length < 2) return '';
  const p = (i: number) => pts[Math.max(0, Math.min(pts.length - 1, i))];
  let d = `M${pts[0][0].toFixed(4)},${pts[0][1].toFixed(4)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = p(i - 1);
    const [x1, y1] = p(i);
    const [x2, y2] = p(i + 1);
    const [x3, y3] = p(i + 2);
    const c1x = x1 + (x2 - x0) / 6;
    const c1y = y1 + (y2 - y0) / 6;
    const c2x = x2 - (x3 - x1) / 6;
    const c2y = y2 - (y3 - y1) / 6;
    d += `C${c1x.toFixed(4)},${c1y.toFixed(4)} ${c2x.toFixed(4)},${c2y.toFixed(4)} ${x2.toFixed(4)},${y2.toFixed(4)}`;
  }
  return d;
}

export const Sparkline = memo(SparklineImpl);
